/** biome-ignore-all lint/style/useNamingConvention: can't kill save backcompat just yet */
import { createMutable } from "solid-js/store";
import type { AchievementKeys } from "~/data/achievements";
import upgrades from "~/data/upgrades";
import { lzwDecode } from "~/js/encode";
import type { SerializableMission, WaitingMission } from "~/js/missions";
import { createNotification } from "~/js/notifications";
import type { ItemName, JackpotGameState, LuckyWheelWins, StatName, TreeUpgradeKey, UpgradeName } from "~/types";

interface OnlineData {
	keyCreated: number;
	key: string | null;
	coinflipGames: object;
	jackpotState: {
		gameId: number;
		rollTimeClient: number;
		lastState?: JackpotGameState;
	};
}

export interface FullUser {
	achievements: Record<AchievementKeys, boolean>;
	achievements_collected: Record<AchievementKeys, boolean>;
	dailyMissionStreak: number;
	discoveredItems: ItemName[];
	favinv: ItemName[];
	inventory: ItemName[];
	luckyWheelWins: LuckyWheelWins[];
	missions: (SerializableMission | WaitingMission)[];
	money: number;
	online: OnlineData;
	prestigeTier: number;
	seasonTimePlayed: number;
	stats: Record<StatName, number>;
	tickets: number;
	tokens: number;
	unlockedCapsules: ItemName[];
	upgrades: Record<UpgradeName, number>;
	upgradesBought: TreeUpgradeKey[];
	xp: number;
	_v: number;
	_ou?: unknown;
}

export function migrateSave(partialUser: Partial<FullUser>): FullUser {
	// Define migrations as functions from version N -> N+1
	const migrations: Array<(u: Partial<FullUser>) => void> = [
		(u) => {
			// Migrate upgrades to have all default values
			u._ou = Object.assign({}, u.upgrades);
			delete u.upgrades;
		},
	];

	const SAVE_VERSION = migrations.length;
	const currentVersion = partialUser._v ?? 0;

	if (currentVersion > SAVE_VERSION) {
		console.log("Unknown save version, attempting to load as-is.");
	} else {
		let nextVersion = currentVersion;
		for (const migrate of migrations.slice(currentVersion)) {
			migrate(partialUser);
			nextVersion++;
			partialUser._v = nextVersion;
		}
	}

	const fullUser: FullUser = {
		_v: SAVE_VERSION,
		_ou: partialUser._ou,
		achievements: partialUser.achievements ?? ({} as Record<AchievementKeys, boolean>),
		achievements_collected: partialUser.achievements_collected ?? ({} as Record<AchievementKeys, boolean>),
		dailyMissionStreak: partialUser.dailyMissionStreak ?? 0,
		discoveredItems: partialUser.discoveredItems ?? [],
		favinv: partialUser.favinv ?? [],
		inventory: partialUser.inventory ?? [],
		luckyWheelWins: partialUser.luckyWheelWins ?? [],
		missions: partialUser.missions ?? [],
		money: partialUser.money ?? 0,
		online: partialUser.online ?? {
			keyCreated: 0,
			key: null,
			coinflipGames: {},
			jackpotState: {
				gameId: 0,
				rollTimeClient: 0,
			},
		},
		prestigeTier: partialUser.prestigeTier ?? 0,
		seasonTimePlayed: partialUser.seasonTimePlayed ?? 0,
		stats: partialUser.stats ?? ({} as Record<StatName, number>),
		tickets: partialUser.tickets ?? 0,
		tokens: partialUser.tokens ?? 0,
		unlockedCapsules: partialUser.unlockedCapsules ?? [],
		upgrades: {
			// lowkey we should be regenerating the upgrades from upgrades bought every load since upgrades may change
			...Object.fromEntries(Object.entries(upgrades).map(([key, value]) => [key, value.default ?? 0])),
			...partialUser.upgrades,
		} as Record<UpgradeName, number>,
		upgradesBought: partialUser.upgradesBought ?? [],
		xp: partialUser.xp ?? 0,
	};
	return fullUser;
}

export let user: FullUser = migrateSave({
	money: 210,
	tickets: 0,
	tokens: 0,
	xp: 0,
	stats: { creation: Date.now() },
	inventory: ["Spectrum Case"],
	upgrades: {} as Record<UpgradeName, number>,
	upgradesBought: [],
	achievements: {} as Record<AchievementKeys, boolean>,
	achievements_collected: {} as Record<AchievementKeys, boolean>,
	luckyWheelWins: [],
});

export function loadSave(e = "2") {
	try {
		if (e === "2") {
			return migrateSave(JSON.parse(lzwDecode(localStorage.localsave)));
		}
		throw new Error("Unknown save version");
	} catch (e) {
		console.error(e);
		delete localStorage._cbid;

		return user;
	}
}

const loadSaveStartTime = performance.now();
if (localStorage.localsave) user = loadSave(localStorage.lsver || "1");
if (!localStorage.localuser) localStorage.localuser = `${Date.now().toString(36)}.${Math.floor(Math.random() * 36 ** 11).toString(36)}`;
user = createMutable(user);
const loadSaveEndTime = performance.now() - loadSaveStartTime;
console.log(`Loading your save took: ${loadSaveEndTime}ms`);

if (loadSaveEndTime > 1e3) {
	createNotification({
		message:
			"Your save file is large and impacting load times and performance. Consider selling items in your inventory to improve performance.",
	});
}
